import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  User as UserIcon, Mail, Sparkles, Save, LogOut,
  Sun, Moon, Bell, Trash2, LayoutDashboard, Pencil,
  Users, UserPlus, ShieldCheck, ChevronDown, MonitorSmartphone, RefreshCw,
  KeyRound, Server, Plus, CheckCircle2, AlertTriangle,
  Globe2, Eye, EyeOff,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { PRODUCT_HOME } from "@/lib/product";
import { useTheme } from "@/components/dashboard/ThemeProvider";
import { ProviderIntegrationAgentPanel } from "@/components/pro/ProviderIntegrationAgentPanel";
import { GoogleCreditVisibilitySettings } from "@/components/provider-credit/GoogleCreditVisibilitySettings";
import { BillingSettingsSection } from "@/components/billing/BillingSettingsSection";
import { useNotifications } from "@/lib/notifications-context";
import { isWalletCloudMode } from "@/lib/runtime-mode";
import { apiRequest, APIError } from "@/lib/api";
import { hostedRequestEmailVerification } from "@/lib/cloud-auth";
import {
  PRO_PROVIDER_OPTIONS,
  createProProvider,
  deleteProProvider,
  proProviderLabel,
  readProProviders,
  rotateProProviderCredentials,
  updateProProvider,
  validateProProvider,
  type ProProviderConnection,
  type ProProviderOption,
} from "@/lib/pro";
import {
  type BillingWorkspaceType,
} from "@/lib/billing";
import {
  providerAccountInputPayload,
  providerAccountScopeErrors,
  providerClearSecretInputValues,
  providerCredentialFieldsForMode,
  providerCredentialRotationInput,
  providerCredentialSecretStatuses,
  providerDescriptorForSlug,
  providerInputDisplayValue,
  providerInputInitialValues,
  providerInputIsList,
  providerInputUsesMultilineEditor,
  providerInputUsesMultilineSecret,
  providerInputValue,
  providerInvalidJSONInputs,
  providerLiteralAllRegionInputs,
  providerMissingRequiredInputs,
  providerProSetupOptions,
  providerSupportsProNetworkVisibility,
  providerSupportsTypedCredentialRotation,
  providerTypedProSetupPayload,
  readProviderRegistry,
  type ProviderInputValue,
  type ProviderInputValues,
  type ProviderRegistryDescriptor,
  type ProviderRegistryInputField,
} from "@/lib/provider-registry";
import {
  isGoogleProviderSlug,
  readGoogleCreditOnboardingProviderID,
} from "@/lib/provider-credit";
import { MICRO, type BroadcastMode, type PricingMode } from "@/lib/genrl";

export const Route = createFileRoute("/settings")({
  head: () => ({ meta: [{ title: "Settings · GENRL" }] }),
  component: SettingsPage,
});

type WorkspaceRole = "viewer" | "admin" | "owner";

type WorkspacePermission =
  | "view_wallets"
  | "view_jobs"
  | "view_pooled_demand"
  | "view_resources"
  | "view_workloads"
  | "view_costs"
  | "view_recommendations"
  | "manage_providers"
  | "launch_compute"
  | "manage_alerts"
  | "execute_low_risk_actions"
  | "execute_high_risk_actions"
  | "approve_actions"
  | "invite_users"
  | "submit_commitments"
  | "billing"
  | "legal_commitments"
  | "manage_roles"
  | "manage_workspace";

type WorkspaceMember = {
  id: string;
  userId?: string;
  name: string;
  email: string;
  role: WorkspaceRole;
  status: "active" | "invited";
  invitedAt: string;
  joinedAt?: string;
};

type CompanyWorkspace = {
  id: string;
  workspaceType: BillingWorkspaceType;
  companyName: string;
  billingEmail: string;
  members: WorkspaceMember[];
  rolePermissions: Record<WorkspaceRole, WorkspacePermission[]>;
  currentMemberId?: string;
  currentRole?: WorkspaceRole;
  currentPermissions: WorkspacePermission[];
};

type SettingsSection = "account" | "workspace" | "billing" | "providers" | "preferences" | "session";

type HostedSession = {
  id: string;
  current: boolean;
  status: "active" | "revoked" | "expired" | "invalid";
  userAgent?: string;
  ipAddress?: string;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
  revokedAt?: string;
};

type HostedSessionsResponse = {
  sessions: HostedSession[];
};

type HostedSecurityEvent = {
  id: string;
  eventType: string;
  severity: "info" | "warning" | "critical" | string;
  sessionId?: string;
  userAgent?: string;
  ipAddress?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
};

type HostedSecurityEventsResponse = {
  events: HostedSecurityEvent[];
};

const ROLE_LABELS: Record<WorkspaceRole, string> = {
  viewer: "Viewer",
  admin: "Admin",
  owner: "Workspace owner",
};

const ROLE_HINTS: Record<WorkspaceRole, string> = {
  viewer: "Can view wallets, usage, pooled demand, jobs, and reports.",
  admin: "Can manage providers, launch compute, create demand, and invite users.",
  owner: "Full workspace control, billing, legal commitments, and role management.",
};

const PERMISSION_LABELS: Record<WorkspacePermission, string> = {
  view_wallets: "View wallets",
  view_jobs: "View jobs",
  view_pooled_demand: "View pooled demand",
  view_resources: "View resources",
  view_workloads: "View workloads",
  view_costs: "View costs",
  view_recommendations: "View recommendations",
  manage_providers: "Manage providers",
  launch_compute: "Launch compute",
  manage_alerts: "Manage alerts",
  execute_low_risk_actions: "Execute low-risk actions",
  execute_high_risk_actions: "Execute high-risk actions",
  approve_actions: "Approve actions",
  invite_users: "Invite users",
  submit_commitments: "Submit commitments",
  billing: "Billing",
  legal_commitments: "Legal commitments",
  manage_roles: "Manage roles",
  manage_workspace: "Manage workspace",
};

const ROLE_PERMISSION_OPTIONS = Object.keys(PERMISSION_LABELS) as WorkspacePermission[];

const DEFAULT_ROLE_PERMISSIONS: Record<WorkspaceRole, WorkspacePermission[]> = {
  viewer: ["view_wallets", "view_jobs", "view_pooled_demand", "view_resources", "view_workloads", "view_costs", "view_recommendations"],
  admin: [
    "view_wallets",
    "view_jobs",
    "view_pooled_demand",
    "view_resources",
    "view_workloads",
    "view_costs",
    "view_recommendations",
    "manage_providers",
    "launch_compute",
    "manage_alerts",
    "execute_low_risk_actions",
    "invite_users",
    "submit_commitments",
  ],
  owner: [
    "view_wallets",
    "view_jobs",
    "view_pooled_demand",
    "view_resources",
    "view_workloads",
    "view_costs",
    "view_recommendations",
    "manage_providers",
    "launch_compute",
    "manage_alerts",
    "execute_low_risk_actions",
    "execute_high_risk_actions",
    "approve_actions",
    "invite_users",
    "submit_commitments",
    "billing",
    "legal_commitments",
    "manage_roles",
    "manage_workspace",
  ],
};

type WorkspaceResponse = {
  workspace: CompanyWorkspace;
};

const WORKSPACE_API = "/api/wallet/accounts?action=workspace";
const HOSTED_SESSIONS_API = "/api/auth/hosted/sessions";
const HOSTED_SECURITY_EVENTS_API = "/api/auth/hosted/security-events";

const PRO_BROADCAST_OPTIONS: Array<{ value: BroadcastMode; label: string }> = [
  { value: "BOTH", label: "Account + network" },
  { value: "BROADCAST_ONLY", label: "Network only" },
  { value: "INTERNAL_ONLY", label: "Private account only" },
];

const PRO_PRICING_OPTIONS: Array<{ value: PricingMode; label: string }> = [
  { value: "PASSTHROUGH", label: "Use provider cost" },
  { value: "PREMIUM_OR_DISCOUNT", label: "Markup / discount" },
  { value: "FIXED", label: "Fixed seller price" },
  { value: "FLOOR_ADJUSTED", label: "Adjusted with floor" },
];

function SettingsPage() {
  const hostedMode = isWalletCloudMode();
  const { user, updateUser, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { unread, clear } = useNotifications();
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name ?? "");
  const [email, setEmail] = useState(user?.email ?? "");
  const [workspace, setWorkspace] = useState<CompanyWorkspace>(() => readWorkspace());
  const [workspaceLoading, setWorkspaceLoading] = useState(true);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<WorkspaceRole>("viewer");
  const [editingRole, setEditingRole] = useState<WorkspaceRole>("viewer");
  const [roleEditorOpen, setRoleEditorOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<SettingsSection>(() => readSettingsSection());
  const [sessions, setSessions] = useState<HostedSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [securityEvents, setSecurityEvents] = useState<HostedSecurityEvent[]>([]);
  const [securityEventsLoading, setSecurityEventsLoading] = useState(false);
  const [revokingSessionId, setRevokingSessionId] = useState("");
  const [emailVerificationBusy, setEmailVerificationBusy] = useState(false);
  const [emailVerificationURL, setEmailVerificationURL] = useState("");
  const [proProviders, setProProviders] = useState<ProProviderConnection[]>([]);
  const [
    googleCreditOnboardingProviderID,
    setGoogleCreditOnboardingProviderID,
  ] = useState(() => readGoogleCreditOnboardingProviderID());
  const [proLoading, setProLoading] = useState(false);
  const [proBusy, setProBusy] = useState("");
  const [proProviderSlug, setProProviderSlug] = useState<ProProviderOption["value"]>("runpod");
  const [proProviderName, setProProviderName] = useState("");
  const [proAPIKey, setProAPIKey] = useState("");
  const [proProviderDescriptors, setProProviderDescriptors] = useState<ProviderRegistryDescriptor[]>([]);
  const [proAuthMode, setProAuthMode] = useState("");
  const [proAuthValues, setProAuthValues] = useState<ProviderInputValues>({});
  const [proAccountValues, setProAccountValues] = useState<ProviderInputValues>({});
  const [proCredentialRotationID, setProCredentialRotationID] = useState("");
  const [proCredentialRotationValues, setProCredentialRotationValues] =
    useState<ProviderInputValues>({});

  const proProviderOptions = useMemo(
    () => providerProSetupOptions(proProviderDescriptors, PRO_PROVIDER_OPTIONS),
    [proProviderDescriptors],
  );
  const selectedProProviderOption = proProviderOptions.find(
    (option) => option.value === proProviderSlug,
  );
  const selectedProProviderAvailable = Boolean(
    selectedProProviderOption && !selectedProProviderOption.disabled,
  );
  const selectedProProviderDescriptor = useMemo(
    () => providerDescriptorForSlug(proProviderDescriptors, proProviderSlug),
    [proProviderDescriptors, proProviderSlug],
  );
  const selectedProCredentialFields = useMemo(
    () => providerCredentialFieldsForMode(selectedProProviderDescriptor, proAuthMode),
    [selectedProProviderDescriptor, proAuthMode],
  );
  const typedProProvider = Boolean(
    selectedProProviderDescriptor && selectedProProviderDescriptor.credentials.mode_specs.length > 0,
  );

  // hydrate when auth becomes ready
  useEffect(() => {
    if (!user) return;
    setName(user.name);
    setEmail(user.email);
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    if (!user) return;
    setWorkspaceLoading(true);
    apiRequest<WorkspaceResponse>(WORKSPACE_API, { method: "GET" })
      .then((payload) => {
        if (cancelled) return;
        setWorkspace(normalizeWorkspace(payload.workspace));
      })
      .catch((error) => {
        if (cancelled) return;
        toast.error(error instanceof Error ? error.message : "Could not load workspace.");
      })
      .finally(() => {
        if (!cancelled) setWorkspaceLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const syncHash = () => setActiveSection(readSettingsSection());
    syncHash();
    window.addEventListener("hashchange", syncHash);
    return () => window.removeEventListener("hashchange", syncHash);
  }, []);

  const loadSessions = useCallback(async () => {
    if (!user) return;
    setSessionsLoading(true);
    try {
      const payload = await apiRequest<HostedSessionsResponse>(HOSTED_SESSIONS_API, { method: "GET" });
      setSessions(normalizeSessions(payload.sessions));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not load sessions.");
    } finally {
      setSessionsLoading(false);
    }
  }, [user]);

  const loadSecurityEvents = useCallback(async () => {
    if (!user) return;
    setSecurityEventsLoading(true);
    try {
      const payload = await apiRequest<HostedSecurityEventsResponse>(HOSTED_SECURITY_EVENTS_API, { method: "GET" });
      setSecurityEvents(normalizeSecurityEvents(payload.events));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not load security events.");
    } finally {
      setSecurityEventsLoading(false);
    }
  }, [user]);

  const loadProSettings = useCallback(async () => {
    if (!user) return;
    setProLoading(true);
    try {
      const [providersPayload, registryPayload] = await Promise.all([
        readProProviders(),
        readProviderRegistry("pro").catch(() => null),
      ]);
      setProProviders(Array.isArray(providersPayload.providers) ? providersPayload.providers : []);
      if (registryPayload?.providers) setProProviderDescriptors(registryPayload.providers);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not load connected providers.");
    } finally {
      setProLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (activeSection === "session") {
      void loadSessions();
      void loadSecurityEvents();
    }
    if (activeSection === "providers") {
      void loadProSettings();
    }
  }, [activeSection, loadProSettings, loadSecurityEvents, loadSessions]);

  useEffect(() => {
    if (!selectedProProviderDescriptor) return;
    const availableModes = selectedProProviderDescriptor.credentials.modes || [];
    const nextMode = availableModes.includes(proAuthMode)
      ? proAuthMode
      : selectedProProviderDescriptor.credentials.default_mode || availableModes[0] || "";
    if (nextMode !== proAuthMode) setProAuthMode(nextMode);
    setProAccountValues((current) =>
      providerInputInitialValues(selectedProProviderDescriptor.account_inputs, current),
    );
  }, [selectedProProviderDescriptor, proAuthMode]);

  useEffect(() => {
    if (!selectedProProviderDescriptor || !proAuthMode) return;
    setProAuthValues((current) => providerInputInitialValues(selectedProCredentialFields, current));
  }, [selectedProProviderDescriptor, proAuthMode, selectedProCredentialFields]);

  const dirtyAccount = name !== (user?.name ?? "") || email !== (user?.email ?? "");
  const canInviteUsers = hasWorkspacePermission(workspace, "invite_users");
  const canManageRoles = hasWorkspacePermission(workspace, "manage_roles");
  const canManageWorkspace = hasWorkspacePermission(workspace, "manage_workspace");
  const activeOtherSessions = sessions.filter((session) => !session.current && session.status === "active");
  const saveAccount = () => {
    updateUser({ name: name.trim() || "Operator", email: email.trim() });
    toast.success("Account updated");
  };

  const resendEmailVerification = async () => {
    setEmailVerificationBusy(true);
    setEmailVerificationURL("");
    try {
      const payload = await hostedRequestEmailVerification();
      if (payload.verified) {
        updateUser({ emailVerified: true });
        toast.success("Email already verified");
      } else {
        if (payload.verification_url) setEmailVerificationURL(payload.verification_url);
        toast.success("Verification link sent");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not send verification link.");
    } finally {
      setEmailVerificationBusy(false);
    }
  };

  const revokeSession = async (sessionId: string) => {
    const target = sessions.find((session) => session.id === sessionId);
    if (!target || target.current || target.status !== "active") return;
    setRevokingSessionId(sessionId);
    try {
      await apiRequest(`${HOSTED_SESSIONS_API}/${encodeURIComponent(sessionId)}/revoke`, {
        method: "POST",
        timeoutMs: 12000,
      });
      toast.success("Session revoked");
      await loadSessions();
      await loadSecurityEvents();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not revoke session.");
    } finally {
      setRevokingSessionId("");
    }
  };

  const revokeOtherSessions = async () => {
    if (activeOtherSessions.length === 0) return;
    setRevokingSessionId("all");
    try {
      const payload = await apiRequest<{ revoked?: number }>(`${HOSTED_SESSIONS_API}/revoke-all`, {
        method: "POST",
        timeoutMs: 12000,
      });
      toast.success(`${Number(payload.revoked || 0)} session${Number(payload.revoked || 0) === 1 ? "" : "s"} revoked`);
      await loadSessions();
      await loadSecurityEvents();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not revoke sessions.");
    } finally {
      setRevokingSessionId("");
    }
  };

  const selectProProvider = (providerSlug: string) => {
    setProProviderSlug(providerSlug);
    setProAuthValues({});
    setProAccountValues({});
    setProAPIKey("");
    const descriptor = providerDescriptorForSlug(proProviderDescriptors, providerSlug);
    setProAuthMode(
      descriptor?.credentials.default_mode || descriptor?.credentials.modes?.[0] || "",
    );
  };

  const setProInputValue = (
    setter: React.Dispatch<React.SetStateAction<ProviderInputValues>>,
    field: ProviderRegistryInputField,
    raw: unknown,
  ) => {
    setter((current) => ({ ...current, [field.key]: providerInputValue(field, raw) }));
  };

  const addProProvider = async () => {
    const option = proProviderOptions.find((item) => item.value === proProviderSlug);
    if (!option || option.disabled) {
      toast.error(option?.hint || "Provider setup is unavailable.");
      return;
    }
    const apiKey = proAPIKey.trim();
    if (!typedProProvider && !apiKey) {
      toast.error("Add the provider API key first.");
      return;
    }
    if (typedProProvider && selectedProProviderDescriptor) {
      const missingCredentials = providerMissingRequiredInputs(
        selectedProCredentialFields,
        proAuthValues,
      );
      const missingAccount = providerMissingRequiredInputs(
        selectedProProviderDescriptor.account_inputs,
        proAccountValues,
      );
      const missing = [...missingCredentials, ...missingAccount];
      const invalidJSON = providerInvalidJSONInputs(
        [...selectedProCredentialFields, ...selectedProProviderDescriptor.account_inputs],
        { ...proAuthValues, ...proAccountValues },
      );
      const literalAllRegions = providerLiteralAllRegionInputs(
        selectedProProviderDescriptor.account_inputs,
        proAccountValues,
      );
      const scopeErrors = providerAccountScopeErrors(proAccountValues);
      if (!proAuthMode || missing.length > 0) {
        toast.error(
          !proAuthMode
            ? "Choose an authentication mode."
            : `Complete the required provider fields: ${missing.join(", ")}.`,
        );
        return;
      }
      if (invalidJSON.length > 0) {
        toast.error(`Enter valid JSON for: ${invalidJSON.join(", ")}.`);
        return;
      }
      if (literalAllRegions.length > 0) {
        toast.error(
          `Enter every canonical region ID explicitly for: ${literalAllRegions.join(", ")}. “all” is not a provider region wildcard.`,
        );
        return;
      }
      if (scopeErrors.length > 0) {
        toast.error(scopeErrors.join(". "));
        return;
      }
    }
    setProBusy("create");
    try {
      const payload = await createProProvider({
        ...(typedProProvider && selectedProProviderDescriptor
          ? providerTypedProSetupPayload({
              provider: selectedProProviderDescriptor,
              mode: proAuthMode,
              credentialValues: proAuthValues,
              accountValues: proAccountValues,
              providerName: option?.label || proProviderSlug,
              displayName:
                proProviderName.trim() || `${option?.label || proProviderSlug} Provider`,
            })
          : {
              provider_slug: proProviderSlug,
              provider_name: option?.label || proProviderSlug,
              display_name:
                proProviderName.trim() || `${option?.label || proProviderSlug} Provider`,
              api_key: apiKey,
              visibility: "private" as const,
            }),
      });
      setProProviders((current) => [payload.provider, ...current.filter((provider) => provider.id !== payload.provider.id)]);
      if (isGoogleProviderSlug(payload.provider.provider_slug)) {
        setGoogleCreditOnboardingProviderID(payload.provider.id);
      }
      if (payload.provider.status === "active") {
        setProProviderName("");
        if (selectedProProviderDescriptor) {
          setProAuthValues(providerInputInitialValues(selectedProCredentialFields));
          setProAccountValues(
            providerInputInitialValues(selectedProProviderDescriptor.account_inputs),
          );
        }
        toast.success("Provider connected");
      } else {
        const status = formatProProviderStatus(payload.provider.status);
        toast.warning(
          payload.provider.last_error
            ? `Provider saved as ${status}: ${payload.provider.last_error}`
            : `Provider saved as ${status}. Review the connection before launching jobs.`,
        );
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not connect provider.");
    } finally {
      setProAPIKey("");
      setProAuthValues((current) =>
        providerClearSecretInputValues(selectedProCredentialFields, current),
      );
      setProBusy("");
    }
  };

  const refreshProProvider = async (providerID: string) => {
    setProBusy(providerID);
    try {
      const payload = await validateProProvider(providerID);
      setProProviders((current) => current.map((provider) => provider.id === providerID ? payload.provider : provider));
      toast.success("Provider checked");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not check provider.");
    } finally {
      setProBusy("");
    }
  };

  const toggleProCredentialRotation = (providerID: string) => {
    setProCredentialRotationID((current) => current === providerID ? "" : providerID);
    setProCredentialRotationValues({});
  };

  const rotateTypedProProviderCredentials = async (provider: ProProviderConnection) => {
    const descriptor = providerDescriptorForSlug(proProviderDescriptors, provider.provider_slug);
    const auth = providerCredentialRotationInput(
      descriptor,
      provider.auth_mode || "",
      proCredentialRotationValues,
    );
    if (!auth) {
      toast.error(
        providerSupportsTypedCredentialRotation(descriptor, provider.auth_mode || "")
          ? "Enter at least one credential field to update."
          : "Typed credential rotation is unavailable for this provider.",
      );
      return;
    }
    setProBusy(`rotate:${provider.id}`);
    try {
      const payload = await rotateProProviderCredentials(provider.id, auth);
      setProProviders((current) => current.map((item) =>
        item.id === provider.id ? payload.provider : item,
      ));
      setProCredentialRotationID("");
      setProCredentialRotationValues({});
      toast.success("Provider credentials rotated");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not rotate provider credentials.");
    } finally {
      if (descriptor) {
        const fields = providerCredentialFieldsForMode(descriptor, provider.auth_mode || "");
        setProCredentialRotationValues((current) =>
          providerClearSecretInputValues(fields, current),
        );
      }
      setProBusy("");
    }
  };

  const editProProviderDraft = (providerID: string, patch: Partial<ProProviderConnection>) => {
    setProProviders((current) => current.map((provider) => (
      provider.id === providerID ? { ...provider, ...patch } : provider
    )));
  };

  const saveProProvider = async (provider: ProProviderConnection) => {
    const descriptor = providerDescriptorForSlug(proProviderDescriptors, provider.provider_slug);
    const requestedVisibility = normalizeProProviderVisibility(provider.visibility);
    if (requestedVisibility === "network" && !descriptor) {
      toast.error("Provider capability metadata is unavailable. Refresh before publishing to the network.");
      return;
    }
    const visibility =
      requestedVisibility === "network" &&
      !providerSupportsProNetworkVisibility(descriptor)
        ? "private"
        : requestedVisibility;
    const broadcastMode: BroadcastMode = visibility === "private"
      ? "INTERNAL_ONLY"
      : provider.broadcast_mode === "BROADCAST_ONLY"
        ? "BROADCAST_ONLY"
        : "BOTH";
    if (provider.pricing_mode === "FIXED" && Number(provider.fixed_unit_price_micros || 0) <= 0) {
      toast.error("Set a fixed seller price before saving.");
      return;
    }
    const accountFields = descriptor?.account_inputs || [];
    const accountValues = providerInputInitialValues(
      accountFields,
      (provider.account || {}) as ProviderInputValues,
    );
    const missingAccount = providerMissingRequiredInputs(accountFields, accountValues);
    const invalidJSON = providerInvalidJSONInputs(accountFields, accountValues);
    const literalAllRegions = providerLiteralAllRegionInputs(accountFields, accountValues);
    const scopeErrors = providerAccountScopeErrors(accountValues);
    if (missingAccount.length > 0) {
      toast.error(`Complete the required account fields: ${missingAccount.join(", ")}.`);
      return;
    }
    if (invalidJSON.length > 0) {
      toast.error(`Enter valid JSON for: ${invalidJSON.join(", ")}.`);
      return;
    }
    if (literalAllRegions.length > 0) {
      toast.error(
        `Enter every canonical region ID explicitly for: ${literalAllRegions.join(", ")}. “all” is not a provider region wildcard.`,
      );
      return;
    }
    if (scopeErrors.length > 0) {
      toast.error(scopeErrors.join(". "));
      return;
    }
    setProBusy(`save:${provider.id}`);
    try {
      const payload = await updateProProvider(provider.id, {
        display_name: provider.display_name,
        ...(descriptor && accountFields.length > 0
          ? { account: providerAccountInputPayload(accountFields, accountValues) }
          : {}),
        visibility,
        broadcast_mode: broadcastMode,
        pricing_mode: provider.pricing_mode || "PASSTHROUGH",
        provider_adjustment_bps: Math.round(Number(provider.provider_adjustment_bps || 0)),
        fixed_unit_price_micros: Math.max(0, Math.round(Number(provider.fixed_unit_price_micros || 0))),
        min_unit_price_micros: Math.max(0, Math.round(Number(provider.min_unit_price_micros || 0))),
        max_unit_price_micros: Math.max(0, Math.round(Number(provider.max_unit_price_micros || 0))),
        reserve_balance_threshold_micros: Math.max(0, Math.round(Number(provider.reserve_balance_threshold_micros || 0))),
        broadcast_stock_limit: Math.max(0, Math.round(Number(provider.broadcast_stock_limit || 0))),
        allowed_regions: provider.allowed_regions || [],
        allowed_hardware: provider.allowed_hardware || [],
      });
      setProProviders((current) => current.map((item) => item.id === provider.id ? payload.provider : item));
      toast.success("Provider settings saved");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save provider settings.");
    } finally {
      setProBusy("");
    }
  };

  const removeProProvider = async (providerID: string) => {
    const provider = proProviders.find((item) => item.id === providerID);
    if (
      !window.confirm(
        `Remove ${provider?.display_name || "this provider"}? Any provider resources must be cleaned up before its credentials can be deleted.`,
      )
    ) {
      return;
    }
    setProBusy(providerID);
    try {
      await deleteProProvider(providerID);
      setProProviders((current) => current.filter((provider) => provider.id !== providerID));
      toast.success("Provider removed");
    } catch (error) {
      if (error instanceof APIError && error.code === "ERR_PRO_PROVIDER_DELETE_CLEANUP_PENDING") {
        await loadProSettings();
        toast.warning(
          "Provider revoked; cleanup is still pending. Credentials are retained only for finalization.",
        );
      } else {
        toast.error(error instanceof Error ? error.message : "Could not remove provider.");
      }
    } finally {
      setProBusy("");
    }
  };

  const saveWorkspaceProfile = async () => {
    if (!canManageWorkspace) {
      toast.error("You do not have permission to change workspace profile details.");
      return;
    }
    try {
      const payload = await apiRequest<WorkspaceResponse>(WORKSPACE_API, {
        method: "PATCH",
        body: {
          companyName: workspace.companyName.trim() || user?.daemon?.company || "Company workspace",
          billingEmail: workspace.billingEmail.trim() || user?.email || "",
        },
      });
      setWorkspace(normalizeWorkspace(payload.workspace));
      toast.success("Workspace profile saved");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save workspace.");
    }
  };

  const inviteMember = async () => {
    if (!canInviteUsers) {
      toast.error("You do not have permission to invite workspace users.");
      return;
    }
    const targetEmail = inviteEmail.trim().toLowerCase();
    const targetName = inviteName.trim() || targetEmail.split("@")[0] || "Invited user";
    if (!targetEmail || !targetEmail.includes("@")) {
      toast.error("Enter a valid email address.");
      return;
    }
    if (workspace.members.some((member) => member.email.toLowerCase() === targetEmail)) {
      toast.error("That user is already in this workspace.");
      return;
    }
    try {
      const payload = await apiRequest<WorkspaceResponse>(`${WORKSPACE_API}&workspace_action=invite`, {
        method: "POST",
        body: {
          name: targetName,
          email: targetEmail,
          role: inviteRole,
        },
      });
      setWorkspace(normalizeWorkspace(payload.workspace));
      setInviteName("");
      setInviteEmail("");
      setInviteRole("viewer");
      toast.success("User invite added");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not invite user.");
    }
  };

  const updateMemberRole = async (id: string, role: WorkspaceRole) => {
    if (!canManageRoles) {
      toast.error("You do not have permission to change member roles.");
      return;
    }
    try {
      const payload = await apiRequest<WorkspaceResponse>(`${WORKSPACE_API}&workspace_action=member_role`, {
        method: "PATCH",
        body: { memberId: id, role },
      });
      setWorkspace(normalizeWorkspace(payload.workspace));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update role.");
    }
  };

  const removeMember = async (id: string) => {
    if (!canManageRoles) {
      toast.error("You do not have permission to remove workspace members.");
      return;
    }
    try {
      const payload = await apiRequest<WorkspaceResponse>(`${WORKSPACE_API}&workspace_action=member`, {
        method: "DELETE",
        body: { memberId: id },
      });
      setWorkspace(normalizeWorkspace(payload.workspace));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not remove member.");
    }
  };

  const toggleRolePermission = async (role: WorkspaceRole, permission: WorkspacePermission) => {
    if (!canManageRoles) {
      toast.error("You do not have permission to edit role permissions.");
      return;
    }
    const nextWorkspace = (() => {
      const rolePermissions = normalizeRolePermissions(workspace.rolePermissions);
      const currentPermissions = rolePermissions[role];
      const nextPermissions = currentPermissions.includes(permission)
        ? currentPermissions.filter((item) => item !== permission)
        : [...currentPermissions, permission];

      return {
        ...workspace,
        rolePermissions: {
          ...rolePermissions,
          [role]: nextPermissions,
        },
      };
    })();
    setWorkspace(nextWorkspace);
    try {
      const payload = await apiRequest<WorkspaceResponse>(`${WORKSPACE_API}&workspace_action=role_permissions`, {
        method: "PATCH",
        body: { rolePermissions: nextWorkspace.rolePermissions },
      });
      setWorkspace(normalizeWorkspace(payload.workspace));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not update permissions.");
      apiRequest<WorkspaceResponse>(WORKSPACE_API, { method: "GET" })
        .then((payload) => setWorkspace(normalizeWorkspace(payload.workspace)))
        .catch(() => {});
    }
  };

  return (
    <div className="wallet-ops-shell flex-1">
      <div className="wallet-ops-container">
        <header className="wallet-ops-topbar">
          <div>
            <p className="wallet-ops-label">Settings</p>
            <p className="mt-2 text-[13px] text-[var(--wallet-ops-subtle)]">Company account, users, and access.</p>
          </div>
        </header>

        <main className="space-y-6 py-6">
        {/* Account */}
        {activeSection === "account" && (
        <section id="account" className="wallet-ops-panel scroll-mt-6 p-6">
          <SectionHeader icon={UserIcon} title="Account" hint="Your personal profile." />
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Display name" icon={UserIcon}>
              <TextInput value={name} onChange={setName} placeholder="Nova Operator" />
            </Field>
            <Field label="Email" icon={Mail}>
              <TextInput value={email} onChange={setEmail} placeholder="ops@your.org" />
            </Field>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] px-4 py-3">
            <div>
              <p className="text-[12px] font-semibold text-[var(--wallet-ops-fg)]">
                Email {user?.emailVerified ? "verified" : "not verified"}
              </p>
              <p className="mt-1 text-[11px] text-[var(--wallet-ops-muted)]">
                {user?.emailVerified ? user.emailVerifiedAt || user.email : user?.email || email}
              </p>
              {emailVerificationURL && (
                <p className="mt-2 break-all font-mono text-[11px] text-[var(--wallet-ops-subtle)]">
                  {emailVerificationURL}
                </p>
              )}
            </div>
            {!user?.emailVerified && (
              <button
                type="button"
                onClick={() => void resendEmailVerification()}
                disabled={emailVerificationBusy}
                className="inline-flex min-h-[34px] items-center gap-2 border border-[var(--wallet-ops-border-strong)] bg-[var(--wallet-ops-surface-2)] px-3 text-[11px] uppercase text-[var(--wallet-ops-fg)] transition hover:border-[var(--wallet-ops-fg-strong)] disabled:cursor-not-allowed disabled:text-[var(--wallet-ops-disabled)]"
              >
                <Mail className="h-3.5 w-3.5" />
                {emailVerificationBusy ? "Sending" : "Resend"}
              </button>
            )}
          </div>
          <div className="mt-5 flex items-center justify-between border-t border-[var(--wallet-ops-border)] pt-4">
            <p className="text-[11px] uppercase tracking-[0.08em] text-[var(--wallet-ops-muted)]">
              Signed in via <span className="font-medium capitalize text-[var(--wallet-ops-fg)]">{user?.provider ?? "—"}</span>
            </p>
            <PrimaryButton onClick={saveAccount} disabled={!dirtyAccount}>
              <Save className="h-3.5 w-3.5" /> Save account
            </PrimaryButton>
          </div>
        </section>
        )}

        {/* Workspace access */}
        {activeSection === "workspace" && (
        <section id="workspace" className="wallet-ops-panel scroll-mt-6 p-6">
          <SectionHeader icon={Users} title="Company workspace" hint="Let multiple people use the same company account." />
          {workspaceLoading && (
            <p className="mb-4 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] px-3 py-2 text-[12px] text-[var(--wallet-ops-muted)]">
              Loading workspace access...
            </p>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Company name" icon={LayoutDashboard}>
	              <TextInput
	                value={workspace.companyName}
	                onChange={(value) => setWorkspace((current) => ({ ...current, companyName: value }))}
	                placeholder={user?.daemon?.company || "Company workspace"}
	                disabled={!canManageWorkspace}
	              />
            </Field>
            <Field label="Billing and legal email" icon={Mail}>
	              <TextInput
	                value={workspace.billingEmail}
	                onChange={(value) => setWorkspace((current) => ({ ...current, billingEmail: value }))}
	                placeholder={user?.email || "legal@company.com"}
	                disabled={!canManageWorkspace}
	              />
            </Field>
          </div>

          <div className="mt-5 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] p-4">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div>
                <p className="wallet-ops-label">Invite user</p>
                <p className="mt-1 text-[12px] text-[var(--wallet-ops-muted)]">
                  Add team members to this shared company account and assign their starting role.
                </p>
              </div>
              <button
	                type="button"
	                onClick={saveWorkspaceProfile}
	                disabled={!canManageWorkspace}
	                className="wallet-ops-button min-h-[34px] px-3 text-[11px]"
              >
                <Save className="h-3.5 w-3.5" />
                Save workspace
              </button>
            </div>
            <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_180px_132px]">
	              <TextInput value={inviteName} onChange={setInviteName} placeholder="Name" disabled={!canInviteUsers} />
	              <TextInput value={inviteEmail} onChange={setInviteEmail} placeholder="email@company.com" disabled={!canInviteUsers} />
	              <RoleSelect value={inviteRole} onChange={setInviteRole} disabled={!canInviteUsers} />
	              <button
	                type="button"
	                onClick={inviteMember}
	                disabled={!canInviteUsers}
	                className="wallet-ops-button min-h-[42px] justify-center px-3 font-semibold"
              >
                <UserPlus className="h-4 w-4" />
                Invite
              </button>
            </div>
          </div>

          <div className="mt-5 divide-y divide-[var(--wallet-ops-border)] border border-[var(--wallet-ops-border)]">
            {workspace.members.map((member) => (
              <div key={member.id} className="grid gap-3 bg-[var(--wallet-ops-surface)] p-4 lg:grid-cols-[minmax(0,1fr)_180px_116px] lg:items-center">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-[14px] font-semibold text-[var(--wallet-ops-fg)]">{member.name}</p>
                    <span className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] text-[var(--wallet-ops-muted)]">
                      {member.status}
                    </span>
                  </div>
                  <p className="mt-1 truncate text-[12px] text-[var(--wallet-ops-muted)]">{member.email}</p>
                  <p className="mt-2 text-[12px] text-[var(--wallet-ops-subtle)]">
                    {formatPermissions(getRolePermissions(workspace.rolePermissions, member.role)) || "No permissions assigned"}
                  </p>
                </div>
	                <RoleSelect value={member.role} onChange={(role) => updateMemberRole(member.id, role)} disabled={!canManageRoles} />
	                <button
	                  type="button"
	                  onClick={() => removeMember(member.id)}
	                  disabled={!canManageRoles}
	                  className="wallet-ops-button min-h-[36px] justify-center px-3 text-[11px]"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          <div className="mt-5 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)]">
            <button
              type="button"
              onClick={() => setRoleEditorOpen((open) => !open)}
              className="flex min-h-[58px] w-full items-center justify-between gap-4 px-4 text-left transition hover:bg-[var(--wallet-ops-surface-2)]"
              aria-expanded={roleEditorOpen}
            >
              <span className="flex items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center border border-[var(--wallet-ops-border-strong)] bg-[var(--wallet-ops-bg)] text-[var(--wallet-ops-fg)]">
                  <ShieldCheck className="h-4 w-4" />
                </span>
                <span>
                  <span className="block text-[12px] font-semibold uppercase tracking-[0.14em] text-[var(--wallet-ops-fg)]">
                    Editable roles
                  </span>
                  <span className="mt-1 block text-[12px] text-[var(--wallet-ops-muted)]">
                    Configure what each workspace role can access.
                  </span>
                </span>
              </span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-[var(--wallet-ops-muted)] transition-transform ${roleEditorOpen ? "rotate-180" : ""}`}
              />
            </button>

            {roleEditorOpen && (
              <div className="border-t border-[var(--wallet-ops-border)] p-4">
                <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
	                  <Field label="Role to edit" icon={ShieldCheck}>
	                    <RoleSelect value={editingRole} onChange={setEditingRole} disabled={!canManageRoles} />
	                  </Field>
                  <div className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] p-3">
                    <p className="text-[14px] font-semibold text-[var(--wallet-ops-fg)]">{ROLE_LABELS[editingRole]}</p>
                    <p className="mt-1 text-[12px] leading-5 text-[var(--wallet-ops-muted)]">{ROLE_HINTS[editingRole]}</p>
                  </div>
                </div>
                <div className="mt-4 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                  {ROLE_PERMISSION_OPTIONS.map((permission) => {
                    const checked = getRolePermissions(workspace.rolePermissions, editingRole).includes(permission);
                    return (
                      <label
                        key={permission}
                        className="flex min-h-[42px] cursor-pointer items-center gap-3 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-[12px] text-[var(--wallet-ops-fg)] transition hover:border-[var(--wallet-ops-border-strong)]"
                      >
                        <input
                          type="checkbox"
	                          checked={checked}
	                          onChange={() => toggleRolePermission(editingRole, permission)}
	                          disabled={!canManageRoles}
	                          className="h-4 w-4 accent-[var(--wallet-ops-fg-strong)]"
                        />
                        <span>{PERMISSION_LABELS[permission]}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
        )}

        {/* Billing */}
        {activeSection === "billing" && (
          <BillingSettingsSection
            workspaceId={workspace.id}
            workspaceName={workspace.companyName || "Personal workspace"}
            workspaceType={workspace.workspaceType}
            roleLabel={workspace.currentRole ? ROLE_LABELS[workspace.currentRole] : "Member"}
            canManageBilling={hasWorkspacePermission(workspace, "billing")}
            currentUserId={user?.id || ""}
            isWorkspaceOwner={workspace.currentRole === "owner"}
          />
        )}

        {/* Connected providers */}
        {activeSection === "providers" && (
        <section id="providers" className="wallet-ops-panel scroll-mt-6 p-6">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <SectionHeader icon={KeyRound} title="Connected providers" hint="Use your own provider accounts for private compute." />
            <button
              type="button"
              onClick={() => void loadProSettings()}
              disabled={proLoading}
              className="inline-flex min-h-[34px] items-center gap-2 border border-[var(--wallet-ops-border-strong)] bg-[var(--wallet-ops-surface-2)] px-3 text-[11px] uppercase text-[var(--wallet-ops-fg)] transition hover:border-[var(--wallet-ops-fg-strong)] disabled:cursor-not-allowed disabled:text-[var(--wallet-ops-disabled)]"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${proLoading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>

          {proLoading && (
            <p className="mb-4 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] px-3 py-2 text-[12px] text-[var(--wallet-ops-muted)]">
              Loading connected providers...
            </p>
          )}

          <div className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] px-4 py-3 text-[12px] leading-5 text-[var(--wallet-ops-muted)]">
            Provider credentials stay connected to your workspace. Network routing and seller
            publication are available for providers whose live capabilities support them.
          </div>

          <ProviderIntegrationAgentPanel providers={proProviderDescriptors} onConnectionCreated={loadProSettings} />

          <div className="mt-5 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] p-4">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="wallet-ops-label">Add provider</p>
                    <p className="mt-1 text-[12px] text-[var(--wallet-ops-muted)]">
                      Credential fields are write-only. Account scope is stored separately and mirrored into workload-ready wallet accounts.
                    </p>
                  </div>
                  <span className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] px-2 py-1 text-[10px] uppercase tracking-[0.08em] text-[var(--wallet-ops-muted)]">
                    Private
                  </span>
                </div>
                <div className="grid gap-3 lg:grid-cols-[180px_minmax(0,1fr)_minmax(0,1fr)_132px]">
                  <Field label="Provider" icon={Server}>
                    <ProProviderSelect
                      value={proProviderSlug}
                      options={proProviderOptions}
                      onChange={selectProProvider}
                    />
                  </Field>
                  <Field label="Display name" icon={Pencil}>
                    <TextInput
                      value={proProviderName}
                      onChange={setProProviderName}
                      placeholder={`${proProviderLabel(proProviderSlug)} Provider`}
                    />
                  </Field>
                  {typedProProvider && selectedProProviderDescriptor ? (
                    <Field label="Authentication" icon={KeyRound}>
                      <select
                        value={proAuthMode}
                        onChange={(event) => {
                          setProAuthMode(event.target.value);
                          setProAuthValues({});
                        }}
                        className="h-11 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-sm text-[var(--wallet-ops-fg)] outline-none transition focus:border-[var(--wallet-ops-border-strong)]"
                      >
                        {selectedProProviderDescriptor.credentials.modes.map((mode) => (
                          <option key={mode} value={mode}>{formatProviderFieldLabel(mode)}</option>
                        ))}
                      </select>
                    </Field>
                  ) : (
                    <Field label="API key" icon={KeyRound}>
                      <TextInput value={proAPIKey} onChange={setProAPIKey} placeholder="Paste provider API key" type="password" />
                    </Field>
                  )}
                  <button
                    type="button"
                    onClick={() => void addProProvider()}
                    disabled={proBusy === "create" || !selectedProProviderAvailable}
                    className="wallet-ops-button mt-[23px] min-h-[42px] justify-center px-3 font-semibold"
                  >
                    <Plus className="h-4 w-4" />
                    {proBusy === "create" ? "Adding" : "Add"}
                  </button>
                </div>
                {selectedProProviderOption?.disabled && (
                  <p className="mt-3 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 py-2 text-[11px] text-[var(--wallet-ops-muted)]">
                    {selectedProProviderOption.hint || "Provider setup is unavailable."}
                  </p>
                )}
                {typedProProvider && selectedProProviderDescriptor && (
                  <div className="mt-4 space-y-4 border-t border-[var(--wallet-ops-border)] pt-4">
                    {selectedProCredentialFields.length > 0 && (
                      <div>
                        <p className="wallet-ops-label">Credential</p>
                        <div className="mt-3 grid gap-3 md:grid-cols-2">
                          {selectedProCredentialFields.map((field) => (
                            <ProviderDescriptorField
                              key={field.key}
                              field={field}
                              value={proAuthValues[field.key]}
                              onChange={(value) => setProInputValue(setProAuthValues, field, value)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedProProviderDescriptor.account_inputs.length > 0 && (
                      <div>
                        <p className="wallet-ops-label">Account scope and launch policy</p>
                        <p className="mt-1 text-[11px] text-[var(--wallet-ops-muted)]">
                          Enter exact provider IDs from your cloud account. Google Cloud may leave its optional region allowlist empty for all accessible regions; Azure requires every canonical region ID explicitly. “all” is never submitted as a region.
                        </p>
                        <div className="mt-3 grid gap-3 md:grid-cols-2">
                          {selectedProProviderDescriptor.account_inputs.map((field) => (
                            <ProviderDescriptorField
                              key={field.key}
                              field={field}
                              value={proAccountValues[field.key]}
                              onChange={(value) => setProInputValue(setProAccountValues, field, value)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-5 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)]">
                <div className="flex items-center justify-between gap-3 border-b border-[var(--wallet-ops-border)] px-4 py-3">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center border border-[var(--wallet-ops-border-strong)] bg-[var(--wallet-ops-bg)] text-[var(--wallet-ops-fg)]">
                      <Server className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[var(--wallet-ops-fg)]">Connected providers</p>
                      <p className="mt-1 text-[12px] text-[var(--wallet-ops-muted)]">Private APIs available to workload launch.</p>
                    </div>
                  </div>
                  <span className="font-mono text-[11px] text-[var(--wallet-ops-muted)]">{proProviders.length}</span>
                </div>
                <div className="divide-y divide-[var(--wallet-ops-border)]">
                  {proProviders.length === 0 && (
                    <div className="px-4 py-6 text-center text-[12px] text-[var(--wallet-ops-muted)]">
                      No provider APIs connected yet.
                    </div>
                  )}
                  {proProviders.map((provider) => {
                    const saving = proBusy === `save:${provider.id}`;
                    const credentialDescriptor = providerDescriptorForSlug(
                      proProviderDescriptors,
                      provider.provider_slug,
                    );
                    const providerSupportsNetworkVisibility =
                      providerSupportsProNetworkVisibility(credentialDescriptor);
                    const networkVisibilityAvailable = providerSupportsNetworkVisibility;
                    const requestedVisibility = normalizeProProviderVisibility(provider.visibility);
                    const visibility = networkVisibilityAvailable ? requestedVisibility : "private";
                    const marketplaceBlocker =
                      credentialDescriptor?.products.marketplace?.blockers?.[0] ||
                      "Network publishing is unavailable for this provider.";
                    const proProduct = credentialDescriptor?.products.pro;
                    const proRolloutBlocker =
                      proProduct && (!proProduct.enabled || !proProduct.ready)
                        ? proProduct.blockers?.[0] || "Provider launches are unavailable."
                        : "";
                    const authMode = String(provider.auth_mode || "").trim();
                    const typedAuthConnection = Boolean(
                      authMode && !["api_key", "static_api_key"].includes(authMode.toLowerCase()),
                    );
                    const canRotateTypedCredentials = providerSupportsTypedCredentialRotation(
                      credentialDescriptor,
                      authMode,
                    );
                    const credentialRotationOpen =
                      proCredentialRotationID === provider.id && canRotateTypedCredentials;
                    const credentialRotationFields = canRotateTypedCredentials
                      ? providerCredentialFieldsForMode(credentialDescriptor, authMode)
                      : [];
                    const credentialSecretStatuses = typedAuthConnection
                      ? providerCredentialSecretStatuses(
                          credentialDescriptor,
                          authMode,
                          provider.credential_status,
                        )
                      : [];
                    const rotatingCredentials = proBusy === `rotate:${provider.id}`;
                    const accountFields = credentialDescriptor?.account_inputs || [];
                    const accountDraftValues = providerInputInitialValues(
                      accountFields,
                      (provider.account || {}) as ProviderInputValues,
                    );
                    return (
                      <div key={provider.id} className="px-4 py-4">
                        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_190px_178px] lg:items-center">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <input
                                value={provider.display_name}
                                onChange={(event) => editProProviderDraft(provider.id, { display_name: event.target.value })}
                                className="h-9 min-w-[220px] max-w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-[13px] font-semibold text-[var(--wallet-ops-fg)] outline-none focus:border-[var(--wallet-ops-border-strong)]"
                              />
                              <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] ${proProviderStatusClass(provider.status)}`}>
                                {provider.status === "active" ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
                                {formatProProviderStatus(provider.status)}
                              </span>
                            </div>
                            <p className="mt-1 truncate text-[12px] text-[var(--wallet-ops-muted)]">
                              {provider.provider_name || proProviderLabel(provider.provider_slug)} · {provider.auth_mode ? `${formatProviderFieldLabel(provider.auth_mode)} credential` : "credential"} {provider.principal_mask || (provider.credential_last4 ? `ending ${provider.credential_last4}` : "stored")}
                            </p>
                            <p className="mt-1 truncate font-mono text-[11px] text-[var(--wallet-ops-subtle)]">
                              wallet {provider.wallet_account_id || provider.id}
                            </p>
                            {provider.last_error && (
                              <p className="mt-1 text-[11px] text-amber-300">{provider.last_error}</p>
                            )}
                            {proRolloutBlocker && (
                              <p className="mt-1 text-[11px] text-amber-300">
                                Launch rollout: {proRolloutBlocker}
                              </p>
                            )}
                            {credentialSecretStatuses.length > 0 && (
                              <p className="mt-1 truncate text-[11px] text-[var(--wallet-ops-subtle)]">
                                {credentialSecretStatuses.map((status) =>
                                  `${status.label}: ${status.configured ? `configured${status.version ? ` v${status.version}` : ""}` : "not configured"}`,
                                ).join(" · ")}
                              </p>
                            )}
                          </div>
                          <div className="text-left text-[11px] text-[var(--wallet-ops-muted)] lg:text-right">
                            <p>{formatProVisibility(visibility)}</p>
                            <p>{provider.last_validated_at ? formatSessionTime(provider.last_validated_at) : "not checked"}</p>
                            <p>{formatProProviderPricing(provider)}</p>
                            <p>{formatProProviderEarnings(provider)}</p>
                            {Number(provider.owner_pending_payout_micros || 0) > 0 && (
                              <p>{formatCreditMicros(provider.owner_pending_payout_micros)} pending payout</p>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 lg:justify-end">
                            {typedAuthConnection && (
                              <button
                                type="button"
                                onClick={() => toggleProCredentialRotation(provider.id)}
                                disabled={!canRotateTypedCredentials || rotatingCredentials || saving}
                                title={canRotateTypedCredentials
                                  ? "Rotate typed credentials"
                                  : credentialDescriptor?.products.pro.blockers?.[0] || "Credential contract unavailable"}
                                className="wallet-ops-button min-h-[34px] justify-center px-3 text-[11px]"
                              >
                                <KeyRound className="h-3.5 w-3.5" />
                                Credentials
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => void refreshProProvider(provider.id)}
                              disabled={proBusy === provider.id || saving || rotatingCredentials}
                              className="wallet-ops-button min-h-[34px] justify-center px-3 text-[11px]"
                            >
                              <RefreshCw className={`h-3.5 w-3.5 ${proBusy === provider.id ? "animate-spin" : ""}`} />
                              Check
                            </button>
                            <button
                              type="button"
                              onClick={() => void saveProProvider(provider)}
                              disabled={saving || rotatingCredentials}
                              className="wallet-ops-button min-h-[34px] justify-center px-3 text-[11px]"
                            >
                              <Save className="h-3.5 w-3.5" />
                              {saving ? "Saving" : "Save"}
                            </button>
                            <button
                              type="button"
                              onClick={() => void removeProProvider(provider.id)}
                              disabled={proBusy === provider.id || saving || rotatingCredentials}
                              className="wallet-ops-button min-h-[34px] justify-center px-3 text-[11px]"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Remove
                            </button>
                          </div>
                        </div>

                        {isGoogleProviderSlug(provider.provider_slug) ? (
                          <GoogleCreditVisibilitySettings
                            provider={provider}
                            autoOpen={
                              provider.id ===
                              googleCreditOnboardingProviderID
                            }
                            onProviderUpdate={(nextProvider) => {
                              setProProviders((current) =>
                                current.map((item) =>
                                  item.id === nextProvider.id
                                    ? {
                                        ...item,
                                        credit_status:
                                          nextProvider.credit_status,
                                      }
                                    : item,
                                ),
                              );
                            }}
                          />
                        ) : null}

                        {credentialRotationOpen && (
                          <div className="mt-4 border border-[var(--wallet-ops-border-strong)] bg-[var(--wallet-ops-bg)] p-4">
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <p className="wallet-ops-label">Rotate credentials</p>
                                <p className="mt-1 text-[11px] text-[var(--wallet-ops-muted)]">
                                  Authentication mode remains {formatProviderFieldLabel(authMode)}. Enter only fields to replace; omitted properties and secrets stay unchanged.
                                </p>
                              </div>
                              <span className="border border-[var(--wallet-ops-border)] px-2 py-1 font-mono text-[10px] text-[var(--wallet-ops-muted)]">
                                v{Math.max(1, Number(provider.credential_version || 1))}
                              </span>
                            </div>
                            {credentialSecretStatuses.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {credentialSecretStatuses.map((status) => (
                                  <span
                                    key={status.key}
                                    className="border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] px-2 py-1 text-[10px] text-[var(--wallet-ops-muted)]"
                                    title={status.rotated_at ? `Last rotated ${formatSessionTime(status.rotated_at)}` : undefined}
                                  >
                                    {status.label} · {status.configured ? "configured" : "not configured"}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div className="mt-3 grid gap-3 md:grid-cols-2">
                              {credentialRotationFields.map((field) => (
                                <ProviderDescriptorField
                                  key={field.key}
                                  field={field}
                                  value={proCredentialRotationValues[field.key]}
                                  preserveExisting
                                  onChange={(value) =>
                                    setProInputValue(setProCredentialRotationValues, field, value)
                                  }
                                />
                              ))}
                            </div>
                            <div className="mt-4 flex flex-wrap justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => toggleProCredentialRotation(provider.id)}
                                disabled={rotatingCredentials}
                                className="wallet-ops-button min-h-[34px] justify-center px-3 text-[11px]"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={() => void rotateTypedProProviderCredentials(provider)}
                                disabled={rotatingCredentials}
                                className="wallet-ops-button min-h-[34px] justify-center px-3 text-[11px] font-semibold"
                              >
                                <KeyRound className="h-3.5 w-3.5" />
                                {rotatingCredentials ? "Validating" : "Rotate"}
                              </button>
                            </div>
                          </div>
                        )}

                        {accountFields.length > 0 && (
                          <details className="mt-4 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] p-4">
                            <summary className="cursor-pointer text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--wallet-ops-fg)]">
                              Account scope and launch defaults
                            </summary>
                            <p className="mt-2 text-[11px] text-[var(--wallet-ops-muted)]">
                              Update non-secret cloud scope here, then save to revalidate it. Azure region allowlists use explicit canonical IDs; Google Cloud may leave its optional allowlist empty. “all” is not accepted.
                            </p>
                            <div className="mt-3 grid gap-3 md:grid-cols-2">
                              {accountFields.map((field) => (
                                <ProviderDescriptorField
                                  key={field.key}
                                  field={field}
                                  value={accountDraftValues[field.key]}
                                  onChange={(value) =>
                                    editProProviderDraft(provider.id, {
                                      account: {
                                        ...(provider.account || {}),
                                        [field.key]: providerInputValue(field, value),
                                      },
                                    })
                                  }
                                />
                              ))}
                            </div>
                          </details>
                        )}

                        <div className="mt-4 grid gap-3 border-t border-[var(--wallet-ops-border)] pt-4 md:grid-cols-3">
                          <Field label="Scope" icon={Globe2}>
                            <select
                              value={visibility}
                              onChange={(event) => {
                                const nextVisibility = normalizeProProviderVisibility(event.target.value);
                                editProProviderDraft(provider.id, {
                                  visibility: nextVisibility,
                                  broadcast_mode: nextVisibility === "private"
                                    ? "INTERNAL_ONLY"
                                    : provider.broadcast_mode === "BROADCAST_ONLY" ? "BROADCAST_ONLY" : "BOTH",
                                });
                              }}
                              className="h-10 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-[12px] text-[var(--wallet-ops-fg)] outline-none focus:border-[var(--wallet-ops-border-strong)]"
                            >
                              <option value="private">Private</option>
                              {networkVisibilityAvailable && <option value="network">Network</option>}
                            </select>
                            {!networkVisibilityAvailable &&
                              (providerSupportsNetworkVisibility || credentialDescriptor?.api_version) && (
                              <p className="mt-1 text-[10px] text-[var(--wallet-ops-subtle)]">
                                {marketplaceBlocker}
                              </p>
                            )}
                          </Field>
                          <Field label="Broadcast" icon={Server}>
                            <select
                              value={visibility === "private" ? "INTERNAL_ONLY" : provider.broadcast_mode || "BOTH"}
                              onChange={(event) => editProProviderDraft(provider.id, { broadcast_mode: event.target.value as BroadcastMode })}
                              disabled={visibility === "private"}
                              className="h-10 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-[12px] text-[var(--wallet-ops-fg)] outline-none focus:border-[var(--wallet-ops-border-strong)] disabled:cursor-not-allowed disabled:text-[var(--wallet-ops-disabled)]"
                            >
                              {PRO_BROADCAST_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </Field>
                          <Field label="Pricing mode" icon={Pencil}>
                            <select
                              value={provider.pricing_mode || "PASSTHROUGH"}
                              onChange={(event) => editProProviderDraft(provider.id, { pricing_mode: event.target.value as PricingMode })}
                              disabled={visibility === "private"}
                              className="h-10 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-[12px] text-[var(--wallet-ops-fg)] outline-none focus:border-[var(--wallet-ops-border-strong)] disabled:cursor-not-allowed disabled:text-[var(--wallet-ops-disabled)]"
                            >
                              {PRO_PRICING_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </Field>
                        </div>

                        <div className="mt-3 grid gap-3 md:grid-cols-5">
                          <Field label="Adjustment %" icon={Sparkles}>
                            <NumberInput
                              value={String(Number(provider.provider_adjustment_bps || 0) / 100)}
                              onChange={(value) => editProProviderDraft(provider.id, { provider_adjustment_bps: Math.round((Number(value) || 0) * 100) })}
                              step="0.1"
                              min="-90"
                              disabled={visibility === "private"}
                            />
                          </Field>
                          <Field label="Fixed price" icon={Pencil}>
                            <NumberInput
                              value={microsToCreditsInput(provider.fixed_unit_price_micros)}
                              onChange={(value) => editProProviderDraft(provider.id, { fixed_unit_price_micros: creditsInputToMicros(value) })}
                              step="0.01"
                              disabled={visibility === "private"}
                            />
                          </Field>
                          <Field label="Min price" icon={Pencil}>
                            <NumberInput
                              value={microsToCreditsInput(provider.min_unit_price_micros)}
                              onChange={(value) => editProProviderDraft(provider.id, { min_unit_price_micros: creditsInputToMicros(value) })}
                              step="0.01"
                              disabled={visibility === "private"}
                            />
                          </Field>
                          <Field label="Max price" icon={Pencil}>
                            <NumberInput
                              value={microsToCreditsInput(provider.max_unit_price_micros)}
                              onChange={(value) => editProProviderDraft(provider.id, { max_unit_price_micros: creditsInputToMicros(value) })}
                              step="0.01"
                              disabled={visibility === "private"}
                            />
                          </Field>
                          <Field label="Stock cap" icon={Server}>
                            <NumberInput
                              value={String(provider.broadcast_stock_limit || "")}
                              onChange={(value) => editProProviderDraft(provider.id, { broadcast_stock_limit: Math.max(0, Math.round(Number(value) || 0)) })}
                              step="1"
                              disabled={visibility === "private"}
                            />
                          </Field>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
        </section>
        )}

        {/* Preferences */}
        {activeSection === "preferences" && (
        <section id="preferences" className="wallet-ops-panel scroll-mt-6 p-6">
          <SectionHeader icon={Sparkles} title="Preferences" hint="Workspace appearance and notifications." />
          <div className="space-y-3">
            <Row
              icon={LayoutDashboard}
              title="Wallet workspace"
              hint="Jump straight back into the wallet product surface."
              action={
                <button
                  onClick={() => navigate({ to: PRODUCT_HOME })}
                  className="inline-flex min-h-[34px] items-center gap-1.5 border border-[var(--wallet-ops-fg-strong)] bg-[var(--wallet-ops-fg-strong)] px-3 text-[11px] font-semibold uppercase text-[var(--wallet-ops-bg)]"
                >
                  <Pencil className="h-3.5 w-3.5" /> Open wallet
                </button>
              }
            />
            <Row
              icon={theme === "dark" ? Moon : Sun}
              title="Appearance"
              hint={`Currently ${theme === "dark" ? "dark" : "light"} mode.`}
              action={
                <button
                  onClick={toggle}
                  className="inline-flex min-h-[34px] items-center border border-[var(--wallet-ops-border-strong)] bg-[var(--wallet-ops-surface-2)] px-3 text-[11px] uppercase text-[var(--wallet-ops-muted)] transition hover:text-[var(--wallet-ops-fg)]"
                >
                  Switch to {theme === "dark" ? "light" : "dark"}
                </button>
              }
            />
            <Row
              icon={Bell}
              title="Notifications"
              hint={`${unread} unread item${unread === 1 ? "" : "s"} in your tray.`}
              action={
                <button
                  onClick={() => { clear(); toast("Notifications cleared"); }}
                  className="inline-flex min-h-[34px] items-center gap-1.5 border border-[var(--wallet-ops-border-strong)] bg-[var(--wallet-ops-surface-2)] px-3 text-[11px] uppercase text-[var(--wallet-ops-muted)] transition hover:text-[var(--wallet-ops-fg)]"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Clear all
                </button>
              }
            />
          </div>
        </section>
        )}

        {/* Danger / session */}
        {activeSection === "session" && (
        <section id="session" className="wallet-ops-panel scroll-mt-6 p-6">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <SectionHeader icon={MonitorSmartphone} title="Sessions" hint="Signed-in browsers for this account." />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void revokeOtherSessions()}
                disabled={activeOtherSessions.length === 0 || revokingSessionId === "all"}
                className="inline-flex min-h-[34px] items-center gap-2 border border-[var(--wallet-ops-border-strong)] bg-[var(--wallet-ops-surface-2)] px-3 text-[11px] uppercase text-[var(--wallet-ops-fg)] transition hover:border-[var(--wallet-ops-fg-strong)] disabled:cursor-not-allowed disabled:text-[var(--wallet-ops-disabled)]"
              >
                <Trash2 className="h-3.5 w-3.5" />
                {revokingSessionId === "all" ? "Revoking" : "Revoke others"}
              </button>
              <button
                type="button"
                onClick={() => {
                  void loadSessions();
                  void loadSecurityEvents();
                }}
                disabled={sessionsLoading || securityEventsLoading}
                className="inline-flex min-h-[34px] items-center gap-2 border border-[var(--wallet-ops-border-strong)] bg-[var(--wallet-ops-surface-2)] px-3 text-[11px] uppercase text-[var(--wallet-ops-fg)] transition hover:border-[var(--wallet-ops-fg-strong)] disabled:cursor-not-allowed disabled:text-[var(--wallet-ops-disabled)]"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${sessionsLoading || securityEventsLoading ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
          </div>

          <div className="divide-y divide-[var(--wallet-ops-border)] border border-[var(--wallet-ops-border)]">
            {sessions.length === 0 && (
              <div className="bg-[var(--wallet-ops-surface)] px-4 py-6 text-center text-[12px] text-[var(--wallet-ops-muted)]">
                {sessionsLoading ? "Loading sessions..." : "No active sessions found."}
              </div>
            )}
            {sessions.map((session) => (
              <div key={session.id} className="grid gap-3 bg-[var(--wallet-ops-surface)] p-4 lg:grid-cols-[minmax(0,1fr)_150px_132px] lg:items-center">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-[14px] font-semibold text-[var(--wallet-ops-fg)]">{formatSessionAgent(session.userAgent)}</p>
                    <span className={`border px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] ${session.current ? "border-[var(--wallet-ops-fg-strong)] bg-[var(--wallet-ops-fg-strong)] text-[var(--wallet-ops-bg)]" : "border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] text-[var(--wallet-ops-muted)]"}`}>
                      {session.current ? "Current" : session.status}
                    </span>
                  </div>
                  <p className="mt-1 truncate font-mono text-[11px] text-[var(--wallet-ops-muted)]">
                    {session.ipAddress || "Unknown IP"} · last seen {formatSessionTime(session.lastSeenAt)}
                  </p>
                  <p className="mt-2 font-mono text-[11px] text-[var(--wallet-ops-subtle)]">
                    {session.id}
                  </p>
                </div>
                <div className="text-left text-[11px] text-[var(--wallet-ops-muted)] lg:text-right">
                  <p>Created {formatSessionTime(session.createdAt)}</p>
                  <p>Expires {formatSessionTime(session.expiresAt)}</p>
                </div>
                {session.current ? (
                  <button
                    type="button"
                    onClick={async () => {
                      await logout();
                      if (!hostedMode) {
                        navigate({ to: "/login" });
                      }
                    }}
                    className="wallet-ops-button min-h-[36px] justify-center px-3 text-[11px]"
                  >
                    <LogOut className="h-3.5 w-3.5" />
                    Sign out
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => void revokeSession(session.id)}
                    disabled={session.status !== "active" || revokingSessionId === session.id}
                    className="wallet-ops-button min-h-[36px] justify-center px-3 text-[11px]"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    {revokingSessionId === session.id ? "Revoking" : "Revoke"}
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="mt-6 border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)]">
            <div className="flex items-center justify-between gap-3 border-b border-[var(--wallet-ops-border)] px-4 py-3">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center border border-[var(--wallet-ops-border-strong)] bg-[var(--wallet-ops-bg)] text-[var(--wallet-ops-fg)]">
                  <ShieldCheck className="h-4 w-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[var(--wallet-ops-fg)]">Security events</p>
                  <p className="mt-1 text-[12px] text-[var(--wallet-ops-muted)]">Recent login and credential activity for this account.</p>
                </div>
              </div>
              <span className="font-mono text-[11px] text-[var(--wallet-ops-muted)]">{securityEvents.length}</span>
            </div>
            <div className="divide-y divide-[var(--wallet-ops-border)]">
              {securityEvents.length === 0 && (
                <div className="px-4 py-6 text-center text-[12px] text-[var(--wallet-ops-muted)]">
                  {securityEventsLoading ? "Loading security events..." : "No security events recorded yet."}
                </div>
              )}
              {securityEvents.map((event) => (
                <div key={event.id} className="grid gap-3 px-4 py-3 lg:grid-cols-[minmax(0,1fr)_180px] lg:items-center">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-[13px] font-semibold text-[var(--wallet-ops-fg)]">{formatSecurityEventType(event.eventType)}</p>
                      <span className={`border px-2 py-0.5 text-[10px] uppercase tracking-[0.08em] ${event.severity === "warning" ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : "border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] text-[var(--wallet-ops-muted)]"}`}>
                        {event.severity || "info"}
                      </span>
                    </div>
                    <p className="mt-1 truncate font-mono text-[11px] text-[var(--wallet-ops-muted)]">
                      {event.ipAddress || "Unknown IP"} · {formatSessionAgent(event.userAgent)}
                    </p>
                    <p className="mt-1 truncate font-mono text-[11px] text-[var(--wallet-ops-subtle)]">
                      {event.sessionId || event.id}
                    </p>
                  </div>
                  <p className="font-mono text-[11px] text-[var(--wallet-ops-muted)] lg:text-right">
                    {formatSessionTime(event.createdAt)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
        )}
        </main>
      </div>
    </div>
  );
}

function readSettingsSection(): SettingsSection {
  if (typeof window === "undefined") return "account";
  const searchSection = new URLSearchParams(window.location.search).get("section") || "";
  const hash = window.location.hash.replace(/^#/, "");
  const candidate = hash || searchSection;
  if (candidate === "pro") {
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}#providers`);
    return "providers";
  }
  if (
    candidate === "workspace" ||
    candidate === "billing" ||
    candidate === "providers" ||
    candidate === "preferences" ||
    candidate === "session" ||
    candidate === "account"
  ) return candidate;
  return "account";
}

function readWorkspace(): CompanyWorkspace {
  return emptyWorkspace();
}

function normalizeRole(value: unknown): WorkspaceRole {
  if (value === "admin" || value === "owner" || value === "viewer") return value;
  return "viewer";
}

function emptyWorkspace(): CompanyWorkspace {
  return {
    id: "",
    workspaceType: "personal",
    companyName: "",
    billingEmail: "",
    members: [],
    rolePermissions: normalizeRolePermissions(null),
    currentPermissions: [],
  };
}

function normalizeWorkspace(value: unknown): CompanyWorkspace {
  const raw = value && typeof value === "object" ? value as Partial<CompanyWorkspace> : {};
  const members = Array.isArray(raw.members) ? raw.members : [];
  const normalizedMembers: WorkspaceMember[] = members
    .map((member: any): WorkspaceMember => ({
      id: String(member?.id || ""),
      userId: String(member?.userId || ""),
      name: String(member?.name || ""),
      email: String(member?.email || "").toLowerCase(),
      role: normalizeRole(member?.role),
      status: member?.status === "invited" ? "invited" : "active",
      invitedAt: String(member?.invitedAt || ""),
      joinedAt: String(member?.joinedAt || ""),
    }))
    .filter((member: WorkspaceMember) => Boolean(member.id && member.email));
  const rawWorkspaceType = String(
    raw.workspaceType ||
    (raw as unknown as { workspace_type?: unknown }).workspace_type ||
    "",
  ).toLowerCase();
  return {
    id: String(raw.id || ""),
    workspaceType: ["organization", "enterprise", "org", "team"].includes(rawWorkspaceType)
        ? "organization"
        : normalizedMembers.length > 1
          ? "organization"
          : "personal",
    companyName: String(raw.companyName || ""),
    billingEmail: String(raw.billingEmail || ""),
    members: normalizedMembers,
    rolePermissions: normalizeRolePermissions(raw.rolePermissions),
    currentMemberId: String(raw.currentMemberId || ""),
    currentRole: raw.currentRole ? normalizeRole(raw.currentRole) : undefined,
    currentPermissions: normalizePermissionList(raw.currentPermissions, []),
  };
}

function normalizeSessions(value: unknown): HostedSession[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item: any): HostedSession => {
      const status = String(item?.status || "").toLowerCase();
      return {
        id: String(item?.id || ""),
        current: Boolean(item?.current),
        status: status === "revoked" || status === "expired" || status === "invalid" ? status : "active",
        userAgent: String(item?.userAgent || ""),
        ipAddress: String(item?.ipAddress || ""),
        createdAt: String(item?.createdAt || ""),
        lastSeenAt: String(item?.lastSeenAt || ""),
        expiresAt: String(item?.expiresAt || ""),
        revokedAt: String(item?.revokedAt || ""),
      };
    })
    .filter((session: HostedSession) => Boolean(session.id));
}

function normalizeSecurityEvents(value: unknown): HostedSecurityEvent[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item: any): HostedSecurityEvent => ({
      id: String(item?.id || ""),
      eventType: String(item?.eventType || "security_event"),
      severity: String(item?.severity || "info"),
      sessionId: String(item?.sessionId || ""),
      userAgent: String(item?.userAgent || ""),
      ipAddress: String(item?.ipAddress || ""),
      metadata: item?.metadata && typeof item.metadata === "object" ? item.metadata : undefined,
      createdAt: String(item?.createdAt || ""),
    }))
    .filter((event: HostedSecurityEvent) => Boolean(event.id));
}

function formatSessionAgent(value: string | undefined): string {
  const raw = String(value || "").trim();
  if (!raw) return "Unknown browser";
  if (/Chrome\//.test(raw) && /Safari\//.test(raw)) return "Chrome browser";
  if (/Firefox\//.test(raw)) return "Firefox browser";
  if (/Version\/.*Safari\//.test(raw)) return "Safari browser";
  if (/Edg\//.test(raw)) return "Edge browser";
  return raw.length > 56 ? `${raw.slice(0, 53)}...` : raw;
}

function formatSessionTime(value: string | undefined): string {
  const raw = String(value || "").trim();
  if (!raw) return "unknown";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "unknown";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatSecurityEventType(value: string | undefined): string {
  const raw = String(value || "").trim();
  switch (raw) {
    case "account_created":
      return "Account created";
    case "login_failed":
      return "Login failed";
    case "login_success":
      return "Login";
    case "logout":
      return "Logout";
    case "session_revoked":
      return "Session revoked";
    case "password_reset_requested":
      return "Password reset requested";
    case "password_reset":
      return "Password reset";
    case "email_verified":
      return "Email verified";
    case "pro_plan_updated":
      return "Provider access mode updated";
    case "pro_provider_connected":
      return "Provider connected";
    case "pro_provider_key_rotated":
      return "Provider key rotated";
    case "pro_provider_updated":
      return "Provider updated";
    case "pro_provider_deleted":
      return "Provider deleted";
    default:
      return raw
        .split("_")
        .filter(Boolean)
        .map((part) => part.slice(0, 1).toUpperCase() + part.slice(1))
        .join(" ") || "Security event";
  }
}

function normalizeProProviderVisibility(value: string | undefined): "private" | "network" {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "network" || raw === "marketplace" || raw === "public") return "network";
  return "private";
}

function formatProVisibility(value: "private" | "network"): string {
  return value === "network" ? "Network eligible" : "Private";
}

function formatProProviderPricing(provider: ProProviderConnection): string {
  switch (provider.pricing_mode) {
    case "FIXED":
      return `fixed ${microsToCreditsInput(provider.fixed_unit_price_micros) || "0"} credits`;
    case "PREMIUM_OR_DISCOUNT":
      return `${Number(provider.provider_adjustment_bps || 0) / 100}% adjustment`;
    case "FLOOR_ADJUSTED":
      return `floor ${microsToCreditsInput(provider.min_unit_price_micros) || "0"} credits`;
    default:
      return "provider cost";
  }
}

function formatProProviderEarnings(provider: ProProviderConnection): string {
  const earned = Number(provider.earned_credit_micros || 0);
  const jobs = Number(provider.settled_jobs || 0);
  if (!Number.isFinite(earned) || earned <= 0) return "no earnings yet";
  return `earned ${formatCreditMicros(earned)}${jobs > 0 ? ` from ${jobs} run${jobs === 1 ? "" : "s"}` : ""}`;
}

function formatCreditMicros(value: number | undefined): string {
  const micros = Number(value || 0);
  if (!Number.isFinite(micros) || micros <= 0) return "0 credits";
  const credits = micros / MICRO;
  return `${credits.toLocaleString(undefined, { maximumFractionDigits: 4 })} credits`;
}

function microsToCreditsInput(value: number | undefined): string {
  const micros = Number(value || 0);
  if (!Number.isFinite(micros) || micros <= 0) return "";
  return String(Math.round(micros) / MICRO);
}

function creditsInputToMicros(value: string): number {
  const credits = Number(value);
  if (!Number.isFinite(credits) || credits <= 0) return 0;
  return Math.round(credits * MICRO);
}

function hasWorkspacePermission(workspace: CompanyWorkspace, permission: WorkspacePermission): boolean {
  return workspace.currentRole === "owner" || workspace.currentPermissions.includes(permission);
}

function normalizeRolePermissions(value: unknown): Record<WorkspaceRole, WorkspacePermission[]> {
  const source = value && typeof value === "object" ? value as Partial<Record<WorkspaceRole, unknown>> : {};
  return {
    viewer: normalizePermissionList(source.viewer, DEFAULT_ROLE_PERMISSIONS.viewer),
    admin: normalizePermissionList(source.admin, DEFAULT_ROLE_PERMISSIONS.admin),
    owner: normalizePermissionList(source.owner, DEFAULT_ROLE_PERMISSIONS.owner),
  };
}

function normalizePermissionList(value: unknown, fallback: WorkspacePermission[]) {
  if (!Array.isArray(value)) return [...fallback];
  const permissions = value
    .map((item) => String(item || "") as WorkspacePermission)
    .filter((item) => ROLE_PERMISSION_OPTIONS.includes(item));
  return permissions.length ? permissions : [...fallback];
}

function getRolePermissions(rolePermissions: Record<WorkspaceRole, WorkspacePermission[]> | undefined, role: WorkspaceRole) {
  return normalizeRolePermissions(rolePermissions)[role];
}

function formatPermissions(permissions: WorkspacePermission[]) {
  return permissions.map((permission) => PERMISSION_LABELS[permission]).filter(Boolean).join(" · ");
}

function formatProProviderStatus(status: string | undefined): string {
  const raw = String(status || "").trim().toLowerCase();
  switch (raw) {
    case "active":
      return "Ready";
    case "validating":
      return "Checking";
    case "degraded":
      return "Needs attention";
    case "suspended":
      return "Suspended";
    case "revoked":
      return "Revoked";
    default:
      return raw || "Unknown";
  }
}

function proProviderStatusClass(status: string | undefined): string {
  switch (String(status || "").trim().toLowerCase()) {
    case "active":
      return "border-[var(--wallet-ops-fg-strong)] bg-[var(--wallet-ops-fg-strong)] text-[var(--wallet-ops-bg)]";
    case "validating":
      return "border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] text-[var(--wallet-ops-muted)]";
    default:
      return "border-amber-500/40 bg-amber-500/10 text-amber-300";
  }
}

/* ---------- primitives ---------- */
function SectionHeader({
  icon: Icon, title, hint,
}: { icon: LucideIcon; title: string; hint: string }) {
  return (
    <div className="mb-5 flex items-start gap-3">
      <div className="flex h-9 w-9 items-center justify-center border border-[var(--wallet-ops-border-strong)] bg-[var(--wallet-ops-surface-2)] text-[var(--wallet-ops-fg)]">
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <h2 className="text-[16px] font-semibold tracking-normal text-[var(--wallet-ops-fg)]">{title}</h2>
        <p className="text-xs text-[var(--wallet-ops-subtle)]">{hint}</p>
      </div>
    </div>
  );
}
function formatProviderFieldLabel(value: string) {
  return String(value || "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function ProviderDescriptorField({
  field,
  value,
  onChange,
  preserveExisting = false,
}: {
  field: ProviderRegistryInputField;
  value?: ProviderInputValue;
  onChange: (value: unknown) => void;
  preserveExisting?: boolean;
}) {
  const [revealSecret, setRevealSecret] = useState(false);
  useEffect(() => {
    if (!providerInputDisplayValue(value)) setRevealSecret(false);
  }, [value]);
  const label = `${field.label || formatProviderFieldLabel(field.key)}${field.required ? " *" : ""}`;
  if (field.type === "boolean") {
    return (
      <Field label={label}>
        <select
          value={String(Boolean(value))}
          onChange={(event) => onChange(event.target.value)}
          className="h-11 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-sm text-[var(--wallet-ops-fg)] outline-none transition focus:border-[var(--wallet-ops-border-strong)]"
        >
          <option value="false">No</option>
          <option value="true">Yes</option>
        </select>
      </Field>
    );
  }
  const listInput = providerInputIsList(field);
  const placeholder = preserveExisting
    ? field.secret
      ? "Enter a replacement; blank keeps the configured secret"
      : "Optional update; blank keeps the stored value"
    : field.options_source
      ? listInput
        ? "Exact values, separated by commas"
        : "Enter the exact provider value"
      : listInput
        ? "Values separated by commas"
        : field.secret
          ? field.type === "secret_file_or_ref"
            ? "Paste secret content; file paths and secret references are not accepted"
            : "Write-only value"
          : "";
  const multilineSecret = providerInputUsesMultilineSecret(field);
  if (providerInputUsesMultilineEditor(field)) {
    return (
      <Field label={label}>
        <div className="relative">
          <textarea
            rows={field.key === "credential_json" || field.type === "json" ? 6 : 3}
            value={providerInputDisplayValue(value)}
            onChange={(event) => onChange(event.target.value)}
            placeholder={placeholder}
            autoComplete="off"
            spellCheck={false}
            style={multilineSecret && !revealSecret ? ({ WebkitTextSecurity: "disc" } as React.CSSProperties) : undefined}
            className={`w-full resize-y border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3.5 py-3 font-mono text-xs text-[var(--wallet-ops-fg)] outline-none transition focus:border-[var(--wallet-ops-border-strong)] placeholder:text-[var(--wallet-ops-disabled)] ${multilineSecret ? "pr-12" : ""}`}
          />
          {multilineSecret && (
            <button
              type="button"
              onClick={() => setRevealSecret((current) => !current)}
              title={revealSecret ? "Hide secret" : "Reveal secret"}
              aria-label={revealSecret ? "Hide secret" : "Reveal secret"}
              className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface-2)] text-[var(--wallet-ops-muted)] hover:text-[var(--wallet-ops-fg)]"
            >
              {revealSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          )}
        </div>
        {field.options_source && (
          <p className="mt-1 font-mono text-[10px] text-[var(--wallet-ops-subtle)]">
            Option source: {field.options_source}
          </p>
        )}
      </Field>
    );
  }
  return (
    <Field label={label}>
      <input
        type={field.secret ? "password" : field.type === "integer" ? "number" : "text"}
        value={providerInputDisplayValue(value)}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoComplete={field.secret ? "new-password" : "off"}
        className="h-11 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3.5 text-sm text-[var(--wallet-ops-fg)] outline-none transition focus:border-[var(--wallet-ops-border-strong)] placeholder:text-[var(--wallet-ops-disabled)]"
      />
      {field.options_source && (
        <p className="mt-1 font-mono text-[10px] text-[var(--wallet-ops-subtle)]">
          Option source: {field.options_source}
        </p>
      )}
    </Field>
  );
}

function Field({
  label, icon: Icon, children,
}: { label: string; icon?: LucideIcon; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-[var(--wallet-ops-muted)]">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </p>
      {children}
    </div>
  );
}
function TextInput({
  value, onChange, placeholder, disabled, type = "text",
}: { value: string; onChange: (v: string) => void; placeholder?: string; disabled?: boolean; type?: string }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="h-11 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3.5 text-sm text-[var(--wallet-ops-fg)] outline-none transition focus:border-[var(--wallet-ops-border-strong)] disabled:cursor-not-allowed disabled:text-[var(--wallet-ops-disabled)] placeholder:text-[var(--wallet-ops-disabled)]"
    />
  );
}
function NumberInput({
  value, onChange, step = "1", min = "0", disabled,
}: { value: string; onChange: (v: string) => void; step?: string; min?: string; disabled?: boolean }) {
  return (
    <input
      type="number"
      min={min}
      step={step}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      className="h-10 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 font-mono text-[12px] text-[var(--wallet-ops-fg)] outline-none transition focus:border-[var(--wallet-ops-border-strong)] disabled:cursor-not-allowed disabled:text-[var(--wallet-ops-disabled)] placeholder:text-[var(--wallet-ops-disabled)]"
    />
  );
}
function ProProviderSelect({
  value,
  options,
  onChange,
}: {
  value: ProProviderOption["value"];
  options: ProProviderOption[];
  onChange: (value: ProProviderOption["value"]) => void;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value as ProProviderOption["value"])}
      className="h-11 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-sm text-[var(--wallet-ops-fg)] outline-none transition focus:border-[var(--wallet-ops-border-strong)]"
    >
      {options.map((option) => (
        <option
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          title={option.hint}
        >
          {option.label}
        </option>
      ))}
    </select>
  );
}
function RoleSelect({ value, onChange, disabled }: { value: WorkspaceRole; onChange: (role: WorkspaceRole) => void; disabled?: boolean }) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(normalizeRole(event.target.value))}
      disabled={disabled}
      className="h-11 w-full border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-bg)] px-3 text-sm text-[var(--wallet-ops-fg)] outline-none transition focus:border-[var(--wallet-ops-border-strong)] disabled:cursor-not-allowed disabled:text-[var(--wallet-ops-disabled)]"
    >
      <option value="viewer">{ROLE_LABELS.viewer}</option>
      <option value="admin">{ROLE_LABELS.admin}</option>
      <option value="owner">{ROLE_LABELS.owner}</option>
    </select>
  );
}

function PrimaryButton({
  onClick, disabled, children,
}: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex min-h-[34px] items-center gap-1.5 border border-[var(--wallet-ops-fg-strong)] bg-[var(--wallet-ops-fg-strong)] px-4 text-[11px] font-semibold uppercase text-[var(--wallet-ops-bg)] transition disabled:cursor-not-allowed disabled:border-[var(--wallet-ops-border-strong)] disabled:bg-[var(--wallet-ops-surface-2)] disabled:text-[var(--wallet-ops-disabled)]"
    >
      {children}
    </button>
  );
}
function Row({
  icon: Icon, title, hint, action,
}: { icon: LucideIcon; title: string; hint: string; action: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border border-[var(--wallet-ops-border)] bg-[var(--wallet-ops-surface)] px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="flex h-8 w-8 items-center justify-center border border-[var(--wallet-ops-border-strong)] bg-[var(--wallet-ops-bg)] text-[var(--wallet-ops-fg)]">
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[var(--wallet-ops-fg)]">{title}</p>
          <p className="text-[11px] text-[var(--wallet-ops-muted)]">{hint}</p>
        </div>
      </div>
      {action}
    </div>
  );
}
